/**
 * parseLedgerCron
 *
 * Reads a five-field cron (minute, hour, day of month, month, day of week) into a `LedgerCron`, or null when it
 * is malformed. Each field is a comma list of `*`, `n` or `a-b`, each optionally stepped by `/step`; a stepped `n`
 * runs from `n` to the field's maximum. Day of week is 0 to 7, and 7 reads as Sunday (0). No names and no `?`.
 * Consumers: `validateLedgerDefinition` (`lifecycle.schedule.cron`) and `ledgerScheduledLifecycle`. Pure.
 */

import type { LedgerCron } from './LedgerLifecycle.types';

type FieldRange = { min: number; max: number };

const FIELDS: readonly FieldRange[] = [
    { min: 0, max: 59 },
    { min: 0, max: 23 },
    { min: 1, max: 31 },
    { min: 1, max: 12 },
    { min: 0, max: 7 },
];

const DIGITS = /^\d+$/;

function wholeNumber(text: string): number | null {
    return DIGITS.test(text) ? Number(text) : null;
}

function readItem(text: string, range: FieldRange): number[] | null {
    const [span, stepText, ...extra] = text.split('/');
    if (span === undefined || extra.length > 0) return null;
    const step = stepText === undefined ? 1 : wholeNumber(stepText);
    if (step === null || step < 1) return null;
    const [fromText, toText, ...beyond] = span.split('-');
    if (fromText === undefined || beyond.length > 0) return null;
    const from = span === '*' ? range.min : wholeNumber(fromText);
    const to = span === '*' ? range.max : toText === undefined ? (stepText === undefined ? from : range.max) : wholeNumber(toText);
    if (from === null || to === null || from < range.min || to > range.max || from > to) return null;
    return Array.from({ length: Math.floor((to - from) / step) + 1 }, (_, index) => from + index * step);
}

function readField(text: string, range: FieldRange): number[] | null {
    const items = text.split(',').map((entry) => readItem(entry, range));
    if (items.some((values) => values === null)) return null;
    return [...new Set(items.flatMap((values) => values ?? []))].sort((a, b) => a - b);
}

function parseLedgerCron(cron: string): LedgerCron | null {
    const texts = cron.trim().split(/\s+/);
    if (texts.length !== FIELDS.length) return null;
    const fields = texts.map((text, index) => {
        const range = FIELDS[index];
        return range === undefined ? null : readField(text, range);
    });
    const [minutes, hours, daysOfMonth, months, daysOfWeek] = fields;
    if (!minutes || !hours || !daysOfMonth || !months || !daysOfWeek) return null;
    return {
        minutes,
        hours,
        daysOfMonth,
        months,
        daysOfWeek: [...new Set(daysOfWeek.map((day) => day % 7))].sort((a, b) => a - b),
        anyDayOfMonth: texts[2] === '*',
        anyDayOfWeek: texts[4] === '*',
    };
}

export { parseLedgerCron };
